import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FiX, FiCheck, FiRefreshCw, FiEdit3, FiZap, FiLock, FiAlertCircle, FiGrid } from 'react-icons/fi';
import { getAgent, getStatus, getAgentSkills, getAgentMcpServers, getEnvironments, updateAgent, listDwAgents, type AgentDetail, type AgentStatus, type AgentSkillRecord, type McpServerRecord, type EnvironmentRecord, } from './claudeAgentApi';
import ClaudeAgentRequestPage from './ClaudeAgentRequestPage';
import ClaudeAgentPendingPage from './ClaudeAgentPendingPage';
function modelLabel(m: AgentDetail['model']): string {
    if (!m)
        return '—';
    return typeof m === 'string' ? m : (m.id ?? '—');
}
const ClaudeMark: React.FC<{
    size?: number;
}> = ({ size = 40 }) => (<svg width={size} height={size} viewBox="0 0 48 48" fill="none" style={{ flexShrink: 0 }}>
    <defs>
      <linearGradient id="ca-ag" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
        <stop offset="0%" stopColor="#fb923c"/>
        <stop offset="100%" stopColor="#c2410c"/>
      </linearGradient>
    </defs>
    <rect width="48" height="48" rx="14" fill="url(#ca-ag)"/>
    <g transform="translate(24,24)">
      {[0, 45, 90, 135, 180, 225, 270, 315].map(a => (<rect key={a} x="-2.6" y="-17" width="5.2" height="12" rx="2.6" fill="rgba(255,255,255,0.95)" transform={`rotate(${a})`}/>))}
    </g>
  </svg>);
const ClaudeAgentAgentPage: React.FC = () => {
    const [agentId, setAgentId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [configured, setConfigured] = useState<boolean | null>(null);
    const [localGate, setLocalGate] = useState<'none' | 'pending'>('none');
    const [agent, setAgent] = useState<AgentDetail | null>(null);
    const [status, setStatus] = useState<AgentStatus | null>(null);
    const [skills, setSkills] = useState<AgentSkillRecord[]>([]);
    const [mcpServers, setMcpServers] = useState<McpServerRecord[]>([]);
    const [env, setEnv] = useState<EnvironmentRecord | null>(null);
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [editorTab, setEditorTab] = useState<'edit' | 'preview'>('edit');
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [saveError, setSaveError] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    useEffect(() => {
        getStatus()
            .then(async (st) => {
            setConfigured(st.configured ?? false);
            if (!st.configured) {
                setStatus(st);
                return;
            }
            const agents = await listDwAgents().catch(() => []);
            const resolvedAgentId = agents[0]?.agent_id;
            if (resolvedAgentId) {
                setAgentId(resolvedAgentId);
                return Promise.all([
                    getAgent(resolvedAgentId),
                    getAgentSkills(resolvedAgentId).catch(() => [] as AgentSkillRecord[]),
                    getAgentMcpServers(resolvedAgentId).catch(() => [] as McpServerRecord[]),
                    getEnvironments().catch(() => [] as EnvironmentRecord[]),
                ]).then(([ag, sk, mc, envs]) => {
                    setAgent(ag);
                    setStatus(st);
                    setSkills(sk);
                    setMcpServers(mc);
                    if (envs.length > 0)
                        setEnv(envs[0]);
                });
            }
            setStatus(st);
        })
            .catch(() => { setConfigured(false); })
            .finally(() => setLoading(false));
    }, []);
    const startEdit = (sys: string) => {
        setDraft(sys);
        setSaveError('');
        setSaved(false);
        setEditorTab('edit');
        setEditing(true);
        setTimeout(() => textareaRef.current?.focus(), 80);
    };
    const handleSave = async () => {
        if (!agentId)
            return;
        setSaving(true);
        setSaveError('');
        try {
            const updated = await updateAgent(agentId, { system: draft });
            setAgent(updated);
            setSaved(true);
            setEditing(false);
        }
        catch (e: any) {
            setSaveError(e.message ?? 'Failed to save');
        }
        finally {
            setSaving(false);
        }
    };
    const insertMd = useCallback((before: string, after: string, linePrefix = '') => {
        const el = textareaRef.current;
        if (!el)
            return;
        const { selectionStart: s, selectionEnd: e, value } = el;
        const selected = value.slice(s, e);
        const replacement = linePrefix
            ? `${linePrefix}${selected || 'text'}`
            : `${before}${selected || 'text'}${after}`;
        setDraft(value.slice(0, s) + replacement + value.slice(e));
        setTimeout(() => {
            const np = s + replacement.length;
            el.setSelectionRange(np, np);
            el.focus();
        }, 0);
    }, []);
    if (!loading && configured === false) {
        if (localGate === 'pending')
            return <ClaudeAgentPendingPage />;
        return (<ClaudeAgentRequestPage onActivated={() => window.location.reload()} onPending={() => setLocalGate('pending')}/>);
    }
    if (loading) {
        return (<div className="ca-fp-page">
        <div className="ca-fp-left">
          <div className="ca-fp-left__header">
            <div className="ca-fp-skel-bone" style={{ width: 56, height: 56, borderRadius: 14 }}/>
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div className="ca-fp-skel-bone" style={{ width: '70%', height: 14 }}/>
              <div className="ca-fp-skel-bone" style={{ width: '45%', height: 11 }}/>
            </div>
          </div>
          <div style={{ padding: '0 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[1, 2, 3, 4].map(i => <div key={i} className="ca-fp-skel-bone" style={{ height: 48, borderRadius: 10 }}/>)}
          </div>
        </div>
        <div className="ca-fp-right">
          <div className="ca-fp-empty-state"><FiZap size={32} style={{ opacity: 0.18 }}/></div>
        </div>
      </div>);
    }
    if (!agent) {
        return (<div className="ca-fp-page">
        <div className="ca-fp-left"/>
        <div className="ca-fp-right">
          <div className="ca-fp-empty-state">
            <ClaudeMark size={48}/>
            <h3>No agent configured</h3>
            <p>Set up your Claude Agent to get started.</p>
          </div>
        </div>
      </div>);
    }
    const infraItems = status ? [
        { label: 'Vault', ok: status.has_vault, warn: false, icon: <FiLock size={13}/> },
        { label: 'SDK', ok: status.sdk_available, warn: false, icon: <FiZap size={13}/> },
        { label: 'MCP Token', ok: status.has_mcp_token, warn: !status.has_mcp_token, icon: <FiAlertCircle size={13}/> },
    ] : [];
    return (<div className="ca-fp-page">

      
      <div className="ca-fp-left">

        
        <div className="ca-fp-left__header" style={{ alignItems: 'flex-start', gap: 14 }}>
          <ClaudeMark size={48}/>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="ca-fp-left__title" style={{ fontSize: 15, marginBottom: 4 }}>{agent.name}</h2>
            <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.38)', fontFamily: "'Poppins',sans-serif", marginBottom: 6 }}>
              {modelLabel(agent.model)}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 5px rgba(34,197,94,0.55)', flexShrink: 0, display: 'inline-block' }}/>
              <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', fontFamily: "'Poppins',sans-serif" }}>Active</span>
            </div>
          </div>
        </div>

        
        <div className="ca-fp-left__list" style={{ padding: '0 14px 20px', gap: 14 }}>

          
          {agentId && (<div className="ca-fp-agent-detail-section">
              <span className="ca-fp-agent-detail-label">Agent ID</span>
              <code className="ca-fp-agent-detail-id">{agentId.slice(0, 24)}…</code>
            </div>)}

          
          <div className="ca-fp-agent-detail-section">
            <span className="ca-fp-agent-detail-label">Overview</span>
            <div className="ca-fp-agent-detail-stats">
              <div className="ca-fp-agent-detail-stat">
                <span className="ca-fp-agent-detail-stat__num">{skills.length}</span>
                <span className="ca-fp-agent-detail-stat__lbl">Skills</span>
              </div>
              <div className="ca-fp-agent-detail-stat__div"/>
              <div className="ca-fp-agent-detail-stat">
                <span className="ca-fp-agent-detail-stat__num">{mcpServers.length}</span>
                <span className="ca-fp-agent-detail-stat__lbl">MCP Servers</span>
              </div>
            </div>
          </div>

          
          {agent.description && (<div className="ca-fp-agent-detail-section">
              <span className="ca-fp-agent-detail-label">Description</span>
              <p className="ca-fp-agent-detail-desc">{agent.description}</p>
            </div>)}

          
          {env && (<div className="ca-fp-agent-detail-section">
              <span className="ca-fp-agent-detail-label">Environment</span>
              <div className="ca-fp-agent-detail-env">
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#22c55e', boxShadow: '0 0 5px rgba(34,197,94,0.5)', flexShrink: 0, display: 'inline-block' }}/>
                {env.name}
              </div>
            </div>)}

          
          {infraItems.length > 0 && (<div className="ca-fp-agent-detail-section">
              <span className="ca-fp-agent-detail-label">Infrastructure</span>
              <div className="ca-fp-agent-detail-infra">
                {infraItems.map(item => (<div key={item.label} className={`ca-fp-agent-detail-infra__row ca-fp-agent-detail-infra__row--${item.ok ? 'ok' : item.warn ? 'warn' : 'err'}`}>
                    {item.icon}
                    <span>{item.label}</span>
                    <span className="ca-fp-agent-detail-infra__dot"/>
                  </div>))}
                {status?.is_byok && (<div className="ca-fp-agent-detail-infra__row ca-fp-agent-detail-infra__row--byok">
                    <FiGrid size={13}/><span>BYOK</span>
                  </div>)}
              </div>
            </div>)}
        </div>
      </div>

      
      <div className="ca-fp-right">
        <div className="ca-fp-editor">

          
          <div className="ca-fp-editor__topbar">
            <div className="ca-fp-editor__topbar-left">
              <ClaudeMark size={36}/>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="ca-fp-editor__skill-title">System Prompt</span>
                  <span className="ca-fp-editor__badge ca-fp-editor__badge--custom">agent</span>
                  {saved && !editing && <span className="ca-fp-editor__badge ca-fp-editor__badge--assigned">Saved</span>}
                </div>
                <span className="ca-fp-editor__skill-sub">
                  {editing
            ? `${draft.length.toLocaleString()} / 10,000 chars`
            : agent.system
                ? `${agent.system.length.toLocaleString()} chars`
                : 'No system prompt set'}
                </span>
              </div>
            </div>
            <div className="ca-fp-editor__topbar-actions">
              {editing ? (<>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => { setEditing(false); setSaveError(''); setSaved(false); }}>
                    <FiX size={13}/> Cancel
                  </button>
                  <button className="ca-fp-editor__btn ca-fp-editor__btn--save" onClick={handleSave} disabled={saving || draft === (agent.system ?? '')}>
                    {saving
                ? <><FiRefreshCw size={12} className="ca-fp-spin"/> Saving…</>
                : <><FiCheck size={12}/> Save</>}
                  </button>
                </>) : (<button className="ca-fp-editor__btn ca-fp-editor__btn--ghost" onClick={() => startEdit(agent.system ?? '')}>
                  <FiEdit3 size={13}/> Edit
                </button>)}
            </div>
          </div>

          
          <div className="ca-fp-editor__body">
            
            <div className="ca-fp-editor__toolbar-row">
              <div className="ca-fp-editor__mode-tabs">
                <button className={`ca-fp-editor__mode-tab${editorTab === 'edit' ? ' ca-fp-editor__mode-tab--on' : ''}`} onClick={() => editing && setEditorTab('edit')}>
                  Edit
                </button>
                <button className={`ca-fp-editor__mode-tab${editorTab === 'preview' ? ' ca-fp-editor__mode-tab--on' : ''}`} onClick={() => setEditorTab('preview')}>
                  Preview
                </button>
              </div>
              {editorTab === 'edit' && (<div className={`ca-fp-md-toolbar${!editing ? ' ca-fp-md-toolbar--disabled' : ''}`}>
                  <button onClick={() => insertMd('**', '**')}><b>B</b></button>
                  <button onClick={() => insertMd('*', '*')}><em>I</em></button>
                  <span className="ca-fp-md-toolbar__div"/>
                  <button onClick={() => insertMd('', '', '# ')}>H1</button>
                  <button onClick={() => insertMd('', '', '## ')}>H2</button>
                  <button onClick={() => insertMd('', '', '### ')}>H3</button>
                  <span className="ca-fp-md-toolbar__div"/>
                  <button onClick={() => insertMd('', '', '- ')}>UL</button>
                  <button onClick={() => insertMd('', '', '1. ')}>OL</button>
                  <span className="ca-fp-md-toolbar__div"/>
                  <button onClick={() => insertMd('`', '`')}>{"`  `"}</button>
                  <button onClick={() => insertMd('', '', '> ')}>❝</button>
                  <button onClick={() => { if (editing) {
            setDraft(d => d + '\n---\n');
            setTimeout(() => textareaRef.current?.focus(), 0);
        } }}>—</button>
                </div>)}
              <span className="ca-fp-editor__charcount">
                {(editing ? draft : (agent.system ?? '')).length.toLocaleString()} chars
              </span>
            </div>

            
            {editorTab === 'edit' ? (editing ? (<textarea ref={textareaRef} className="ca-fp-editor__textarea" value={draft} onChange={e => { setDraft(e.target.value); setSaved(false); setSaveError(''); }} maxLength={10000} placeholder="You are a helpful insurance assistant…" spellCheck={false}/>) : (<pre className="ca-fp-editor__textarea ca-fp-editor__textarea--readonly" onClick={() => startEdit(agent.system ?? '')} title="Click to edit" style={{ cursor: 'pointer', overflow: 'auto' }}>
                  {agent.system || <span style={{ color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>No system prompt set. Click to add one.</span>}
                </pre>)) : (<div className="ca-fp-editor__preview">
                {(editing ? draft : (agent.system ?? '')).trim()
                ? (editing ? draft : (agent.system ?? '')).split('\n').map((line, i) => {
                    if (/^### (.+)/.test(line))
                        return <h3 key={i} className="cac-md-h3">{line.replace(/^### /, '')}</h3>;
                    if (/^## (.+)/.test(line))
                        return <h2 key={i} className="cac-md-h2">{line.replace(/^## /, '')}</h2>;
                    if (/^# (.+)/.test(line))
                        return <h1 key={i} className="cac-md-h1">{line.replace(/^# /, '')}</h1>;
                    if (/^[-*] (.+)/.test(line))
                        return <div key={i} className="cac-md-li">• {line.replace(/^[-*] /, '')}</div>;
                    if (/^\d+\. (.+)/.test(line))
                        return <div key={i} className="cac-md-li">{line}</div>;
                    if (/^> (.+)/.test(line))
                        return <blockquote key={i} className="cac-md-quote">{line.replace(/^> /, '')}</blockquote>;
                    if (/^---$/.test(line.trim()))
                        return <hr key={i} className="cac-md-hr"/>;
                    if (line.trim() === '')
                        return <div key={i} className="cac-md-br"/>;
                    const html = line
                        .replace(/`([^`]+)`/g, '<code>$1</code>')
                        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\*(.+?)\*/g, '<em>$1</em>');
                    return <p key={i} className="cac-md-p" dangerouslySetInnerHTML={{ __html: html }}/>;
                })
                : <span className="ca-fp-editor__preview-empty">Nothing to preview yet.</span>}
              </div>)}

            {saveError && <p className="ca-fp-editor__error">{saveError}</p>}
            {!editing && <p className="ca-fp-editor__hint">Click anywhere in the editor to start editing</p>}
          </div>

        </div>
      </div>
    </div>);
};
export default ClaudeAgentAgentPage;
